import { IRoom, NAME, RemoteStreamType } from '../index';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT, { InnerEventTypes } from '../inner-emitter/inner-event';
import DurationItem from './duration-item';

/**
 * 通话时长统计模块
 * 记录音频、视频（以 track 为基础）的拉流时长
 *  - 订阅成功开始计时
 *  - 取消订阅或者远端取消推流停止计时
 *  - 重新订阅或远端重新推流重新计时
 *  - 退房时统计音频、视频的拉流时长，供其他模块读取，随后清理数据
 * @class CallDurationCalculator
 */
class CallDurationCalculator {
  private _room: IRoom | null = null;
  private _durationMap: Map<string, {userId: string, type: RemoteStreamType, audio: DurationItem[], video: DurationItem[]}>;
  private _eventMap: Map<INNER_EVENT, (...args: any) => void> = new Map();
  constructor(options: { room: IRoom }) {
    this._room = options.room;
    // 统计流在进房后通话时间，区分音频、视频的通话时间。中途取消订阅、远端取消推流不清理数据，在退房时清除数据。
    // Map<key(`${userId_streamType}`), { userId, type, audio: DurationItem[], video: DurationItem[] }>
    this._durationMap = new Map();
    this.installEvents();
  }

  installEvents() {
    this._eventMap
      .set(INNER_EVENT.REMOTE_TRACK_SUBSCRIBED, this.handleSubscribed)
      .set(INNER_EVENT.REMOTE_TRACK_UNSUBSCRIBED, this.handleUnsubscribed)
      .set(INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED, ({ room, prevMuteState, muteState }: InnerEventTypes[INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED][0]) => {
        const { userId } = muteState;
        if (!this.hitTest(room)) return;

        // remote remove track
        if (prevMuteState.hasAudio && !muteState.hasAudio) {
          this.stopDurationItem(`${userId}_${RemoteStreamType.Main}`, NAME.AUDIO);
        }
        if (prevMuteState.hasVideo && !muteState.hasVideo) {
          this.stopDurationItem(`${userId}_${RemoteStreamType.Main}`, NAME.VIDEO);
        }
        if (prevMuteState.hasAuxiliary && !muteState.hasAuxiliary) {
          this.stopDurationItem(`${userId}_${RemoteStreamType.Aux}`, NAME.VIDEO);
        }
        const remotePublishedUser = this._room?.remotePublishedUserMap.get(userId);
        if (!remotePublishedUser) return;
        // remote add track
        if (!prevMuteState.hasAudio && muteState.hasAudio && remotePublishedUser.remoteAudioTrack.isSubscribed) {
          this.addDuractionItem(userId, NAME.AUDIO, RemoteStreamType.Main);
        }
        if (!prevMuteState.hasVideo && muteState.hasVideo && remotePublishedUser.remoteVideoTrack.isSubscribed) {
          this.addDuractionItem(userId, NAME.VIDEO, RemoteStreamType.Main);
        }
        if (!prevMuteState.hasAuxiliary && muteState.hasAuxiliary && remotePublishedUser.remoteAuxiliaryTrack.isSubscribed) {
          this.addDuractionItem(userId, NAME.VIDEO, RemoteStreamType.Aux);
        }
      })
      .set(INNER_EVENT.LEAVE_SUCCESS, ({ room }: InnerEventTypes[INNER_EVENT.LEAVE_SUCCESS][0]) => {
        if (this.hitTest(room)) {
          this.reset();
        }
      })
      .set(INNER_EVENT.SWITCH_ROOM_SUCCESS, ({ room }: InnerEventTypes[INNER_EVENT.SWITCH_ROOM_SUCCESS][0]) => {
        if (this.hitTest(room)) {
          this.reset();
        }
      });
    this._eventMap.forEach((listener, eventName) => innerEmitter.on(eventName, listener, this));
  }

  uninstallEvents() {
    this._eventMap.forEach((listener, eventName) => innerEmitter.off(eventName, listener, this));
    this._eventMap.clear();
  }

  /**
   * 远端流已订阅，开始统计音视频的通话时长
   * @memberof CallDurationCalculator
   */
  handleSubscribed({ track }: InnerEventTypes[INNER_EVENT.REMOTE_TRACK_SUBSCRIBED][0]) {
    if (!this.hitTest(track.room)) return;

    const { userId, streamType, kind } = track;
    if (track.isSubscribed) {
      this.addDuractionItem(userId, kind, streamType);
    } else {
      this.stopDurationItem(`${userId}_${streamType}`, kind);
    }
  }

  handleUnsubscribed({ track }: InnerEventTypes[INNER_EVENT.REMOTE_TRACK_UNSUBSCRIBED][0]) {
    if (!this.hitTest(track.room)) return;
    this.stopDurationItem(`${track.userId}_${track.streamType}`, track.kind);
  }

  /**
   * 判断是否有未结束的 CallStateItem
   * @param {DurationItem[]} options
   * @return {Boolean}
   * @memberof CallDurationCalculator
   */
  isRecording(options: DurationItem[]) {
    return options.findIndex(item => item.endTime === 0) >= 0;
  }

  addDuractionItem(userId: string, kind: AudioOrVideo, streamType: RemoteStreamType) {
    const key = `${userId}_${streamType}`;
    const newDurationItem = new DurationItem();
    const data = this._durationMap.get(key);
    if (data) {
      if (!this.isRecording(data[kind])) {
        data[kind].push(newDurationItem);
      }
    } else {
      this._durationMap.set(key, {
        userId,
        type: streamType,
        audio: kind === NAME.AUDIO ? [newDurationItem] : [],
        video: kind === NAME.AUDIO ? [] : [newDurationItem]
      });
    }
  }

  /**
   * 停止 durationItem
   * @param {String} key
   * @param {'audio'|'video'} type
   * @memberof CallDurationCalculator
   */
  stopDurationItem(key: string, type: AudioOrVideo) {
    if (this._durationMap.has(key)) {
      const data = this._durationMap.get(key);
      const durationItem = data![type].find(item => item.endTime === 0);
      if (durationItem) {
        durationItem.stop();
      }
    }
  }

  hitTest(room?: IRoom) {
    return this._room === room;
  }

  /**
   * 获取某条流的音频、视频拉流时长
   * @param {String} key `${userId}_${streamType}`
   * @param {'audio'|'video'} type
   * @memberof CallDurationCalculator
   */
  getDuration(key: string, type: AudioOrVideo) {
    if (this._durationMap.has(key)) {
      const data = this._durationMap.get(key);
      return data![type].reduce((result, durationItem) => result + durationItem.getDuration(), 0);
    }
    return 0;
  }

  getDurationMap() {
    return this._durationMap;
  }

  // 清理状态
  reset() {
    this._durationMap.clear();
  }

  destroy() {
    this._room = null;
    this.uninstallEvents();
  }
}

export default CallDurationCalculator;
